import { useCallback, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";

import dayjs from "dayjs";
import debounce from "lodash.debounce";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { OlgaEnvironmentsAPI } from "../providers/OlgaEnvironmentsAPI";
import { Button } from "../shared/components/Button";
import { Drawer } from "../shared/components/Drawer";
import { Empty } from "../shared/components/Empty";
import { Tabs } from "../shared/components/Tabs";
import { Title } from "../shared/components/Title";
import { If } from "../shared/components/control-statements";
import { ForEach } from "../shared/components/control-statements/ForEach";
import { Icon } from "../shared/components/core/Icon";
import { EvaluatedOrders } from "../shared/containers/avaliacoes/EvaluatedOrders";
import { Indicators } from "../shared/containers/avaliacoes/Indicators";
import { Environment } from "../shared/core/Environment";
import styles from "../styles/avaliacoes.module.scss";

import { Loading } from "@components";
import { Input } from "../shared/components/Input";
import BrandsFilter from "../shared/containers/avaliacoes/BrandsFilter";
import EvaluateFilter from "../shared/containers/avaliacoes/EvaluateFilter";
import TimeFilter from "./Reports/TimeFilter";
import { InputSearch } from "shared/components/InputSearch";

dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.locale("pt-br");

const tabs = {
  RATING: {
    key: "Avaliação",
    name: "Avaliação",
  },
  PERIOD: {
    key: "Período",
    name: "Período",
  },
  BRAND: {
    key: "Marcas",
    name: "Marcas",
  },
};

const ratingFilters = {
  1: {
    label: "1,0",
    values: [1],
  },
  2: {
    label: "2,0",
    values: [2],
  },
  3: {
    label: "1,0 - 2,0",
    values: [1, 2],
  },
  4: {
    label: "2,0 - 3,0",
    values: [2, 3],
  },
  5: {
    label: "3,0 - 4,0",
    values: [3, 4],
  },
  6: {
    label: "4,0 - 5,0",
    values: [4, 5],
  },
  7: {
    label: "Todas",
    values: [1, 2, 3, 4, 5],
  },
};

export default function Avaliacoes() {
  const [comment, setComment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [evaluations, setEvaluations] = useState(null);
  const [averageAll, setAverageAll] = useState(null);
  const [filters, setFilters] = useState([]);
  const [filterParams, setFilterParams] = useState({});
  const [activeTab, setActiveTab] = useState(tabs.RATING.key);
  const [units, setUnits] = useState({});
  const [brands, setBrands] = useState({});
  const [hasError, setHasError] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState(null);
  const [filterValues, setFilterValues] = useState(null);

  const loadBrandAndUnits = async () => {
    const [unitResponse, brandsResponse] = await Promise.all([
      OlgaEnvironmentsAPI.getUnits(),
      OlgaEnvironmentsAPI.getBrands(),
    ]);

    if (
      brandsResponse.getCode(500) != 200 ||
      unitResponse.getCode(500) != 200
    ) {
      setHasError(true);
      return;
    }

    const brandList = brandsResponse.getData([]);
    const unitList = unitResponse.getData([]);

    const unitsObject = {};
    unitList.map((u) => {
      unitsObject[u.id] = u;
    });

    const brandsObject = {};
    brandList.map((b) => {
      brandsObject[b.id] = b;
    });

    setUnits(unitsObject);
    setBrands(brandsObject);
  };

  const loadEvaluations = async () => {
    setLoading(true);
    await loadBrandAndUnits();

    const ratingValues = [];
    filters.map((filter) => {
      ratingValues.push(...ratingFilters[filter]?.values);
    });
    const overall_average = Array.from(new Set(ratingValues));

    const response = await OlgaEnvironmentsAPI.getEvaluation(Environment.id, {
      ...filterParams,
      ...(overall_average.length > 0 ? { overall_average } : {}),
    });
    const data = response.getData(null);

    if (response.getCode(500) != 200) {
      setHasError(true);
      return;
    }

    setEvaluations(data);
    setAverageAll(data?.response[0]?.overall_average);
    setLoading(false);
  };

  useEffect(() => {
    !evaluations && loadEvaluations();
  }, [evaluations]);

  useEffect(() => {
    if (!openDrawer) {
      loadEvaluations();
    }
  }, [filters, filterParams]);

  const debouncedSearch = useCallback(
    debounce((nextValue) => {
      setFilterParams({ ...filterParams, obs: nextValue || null });
    }, 1000),
    []
  );

  const selectFilterComment = (value) => {
    setFilterParams({ ...filterParams, obs: value });
  };

  const selectRating = (value) => {
    const index = filters.indexOf(value);
    const f = [...filters];

    if (index > -1) {
      f.splice(index, 1);
    } else {
      f.push(value);
    }

    setFilters(f);
  };

  const onUpdateFilters = (filterValues) => {
    const newFilterParams = { ...filterParams };

    Object.keys(filterValues).map((filterKey) => {
      const filterValue = filterValues[filterKey];
      if (filterValue === null) {
        delete newFilterParams[filterKey];
      } else {
        newFilterParams[filterKey] = filterValue;
      }
    });

    setFilterParams(newFilterParams);
  };

  const exportEvaluations = async () => {
    const rating_values = [];
    filters.forEach((f) => rating_values.push(...ratingFilters[f]?.values));

    const overall_average = Array.from(new Set(rating_values));
    const response = await OlgaEnvironmentsAPI.exportEvaluation(
      Environment.id,
      {
        ...filterParams,
        ...(overall_average.length > 0 ? { overall_average } : {}),
      }
    );

    const { link: url } = response.getData({ link: null });
    if (!url) {
      return null;
    }

    const link = document.createElement("a");
    link.href = url;
    link.click();
    link.remove();
  };

  // const getSubtitle = () => {
  // 	const environment = EnvironmentStorage.getEnvironment();

  // 	if (environment?.brand) return `${environment.brand.name}`;
  // 	return environment?.name;
  // };

  const filtersRef = useRef(null);

  const Filters = () => {
    const startDate = dayjs.utc(filterParams?.begin);
    const endDate = dayjs.utc(filterParams?.end);

    const fullMonthStart = startDate.startOf("month");
    const fullMonthEnd = startDate.endOf("month");

    let display;

    if (
      startDate.isSame(fullMonthStart, "day") &&
      endDate.isSame(fullMonthEnd, "day")
    ) {
      display = startDate.format("MMMM [de] YYYY");
    } else {
      display = `${startDate.format("DD/MM/YYYY HH:mm")} - ${endDate.format(
        "DD/MM/YYYY HH:mm"
      )}`;
    }

    return (
      <div className={styles.filters}>
        <If cond={filters?.length > 0}>
          <ForEach
            items={filters}
            render={(item) => (
              <div className={styles.filter}>
                <div className={styles.star}>
                  <i className="fas fa-star" />
                </div>
                <p className={styles.name}>{ratingFilters[item]?.label}</p>
                <div
                  className={styles.remove}
                  onClick={() => selectRating(item)}
                >
                  <i className="far fa-times" />
                </div>
              </div>
            )}
          />
        </If>

        <If
          cond={
            filterParams.brands?.length > 0 || filterParams.units?.length > 0
          }
        >
          <div className={styles.filter}>
            <p className={styles.name}>
              {filterParams.brands?.length > 0
                ? `Marcas (${filterParams.brands?.length}) `
                : ""}
            </p>
            <p className={styles.name}>
              &nbsp;
              {filterParams.units?.length > 0
                ? `/ Unidades (${filterParams.units?.length})`
                : ""}
            </p>
            <div
              className={styles.remove}
              onClick={() => onUpdateFilters({ brands: null, units: null })}
            >
              <i className="far fa-times" />
            </div>
          </div>
        </If>

        <If cond={filterParams.begin && filterParams.end}>
          <div className={styles.filter}>
            <div className="d-flex align-center">
              <span className={styles.label}>Período:</span>
              <If cond={filterParams.period === "FROM_BEGGINING"}>
                <span className={styles.value}>Todos</span>
              </If>
              <If cond={filterParams.period === "FROM_THE_BEGINNING"}>
                Desde o início
              </If>
              <If cond={filterParams.period !== "FROM_THE_BEGINNING"}>
                <span className={styles.value}>{display}</span>
              </If>
            </div>
            <div
              className={styles.remove}
              onClick={() => onUpdateFilters({ begin: null, end: null })}
            >
              <i className="far fa-times" />
            </div>
          </div>
        </If>
      </div>
    );
  };

  return hasError ? (
    <Empty>
      <Icon name="exclamation-circle" size="4x" light />
      <p>Ops!</p>
      <span>
        Ocorreu um erro ao carregar essa página. Verifique as configurações e
        tente novamente!
      </span>
    </Empty>
  ) : (
    <>
      <If cond={loading}>
        <div className={styles.loading}>
          <Loading />
        </div>
      </If>

      <If cond={!loading}>
        <Title
          title={"Avaliações"}
          showSearch
          onSearch={(text) => {}}
          backTo={{
            url: "/fidelizacao-e-marketing",
            name: "Fidelização e Marketing",
          }}
        >
          <If cond={!isMobile}>
            <Filters
              onRemoveFilter={(filter) => {
                filtersRef.current?.removeFilter(filter);
              }}
              filters={filters}
            />
            <InputSearch
              design="two"
              placeholder="Buscar por..."
              className="mb-0 pt-0 mr-10"
              value={comment}
              onChange={(e) => {
                const text = e.target.value;
                setComment(text);
                debouncedSearch(text);
              }}
            />
            <Button
              design="default-icon-mini"
              onClick={() => setOpenDrawer(true)}
            >
              <i className="fal fa-sliders" />
            </Button>
            <Button
              design="default-icon-mini"
              className={"ml-10"}
              onClick={() => exportEvaluations()}
            >
              <Icon name="download" size="lg" />
            </Button>
          </If>
        </Title>

        <If cond={evaluations?.response?.length > 0 && !loading}>
          <Indicators average={evaluations?.response} />

          <EvaluatedOrders
            average={evaluations?.response}
            units={units}
            brands={brands}
            loading={loading}
          />
        </If>
      </If>

      <If
        cond={(!evaluations || evaluations?.response?.length <= 0) && !loading}
      >
        {filterParams ? (
          <Empty>
            <Icon name="list-timeline" regular size="4x" />
            <p>Nenhum registro encontrado.</p>
          </Empty>
        ) : (
          <Empty>
            <Icon name="list-timeline" regular size="4x" />
            <p>Você ainda não tem avaliações.</p>
            <span>Todas as avaliações irão aparecer aqui.</span>
          </Empty>
        )}
      </If>

      <Drawer
        title="Filtrar por"
        visible={openDrawer}
        onClose={() => setOpenDrawer(false)}
        footer={
          <Button
            className="w-100"
            design="primary"
            text={"Filtrar"}
            onClick={() => {
              loadEvaluations();
              setOpenDrawer(false);
            }}
          />
        }
      >
        <div>
          <Tabs
            full
            tabs={Object.values(tabs)}
            onTabSelected={(tab) => setActiveTab(tab)}
            activeTab={activeTab}
          />
        </div>

        <div>
          <If cond={activeTab === tabs.RATING.key}>
            <EvaluateFilter
              selectRating={(value) => selectRating(value)}
              selectFilterComment={(value) => selectFilterComment(value)}
              filters={filters}
              filterParams={filterParams}
            />
          </If>
        </div>

        <If cond={activeTab === tabs.PERIOD.key}>
          <TimeFilter
            onUpdateFilters={(filterValues) => {
              onUpdateFilters(filterValues);
            }}
            onUpdateFilterValues={(values, selectedFilterType) => {
              setSelectedFilterType(selectedFilterType);
              setFilterValues(values);
            }}
            values={filterValues}
            filterType={selectedFilterType}
          />
        </If>

        <If cond={activeTab === tabs.BRAND.key}>
          <BrandsFilter
            environmentBrands={brands}
            environmentUnits={units}
            onChangeBrandsAndUnits={(brands, units) =>
              onUpdateFilters({ units: units, brands: brands })
            }
            filterParams={filterParams}
          />
        </If>
      </Drawer>
    </>
  );
}
